import React, { useRef, useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { IconButton, CircularProgress, useMediaQuery, Avatar } from "@mui/material";
import { PlayArrow, Pause, GetApp, Speed, Person, Mic } from "@mui/icons-material";

const LS_NAME = 'audioMessageRate';

const useStyles = makeStyles(theme => ({
  // Desktop styles
  desktopContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.type === 'light' ? "#f0f0f0" : "#2c2f33",
    padding: theme.spacing(1),
    borderRadius: "8px",
    width: "100%",
    gap: theme.spacing(1),
    maxWidth: "400px",
    boxSizing: "border-box",
    [theme.breakpoints.down('sm')]: {
      width: "100%",
      maxWidth: "280px", // Largura máxima menor para mobile
      padding: "8px",
      gap: "8px",
      boxSizing: "border-box",
      overflow: "hidden",
    }
  },
  avatar: {
    width: 40,
    height: 40,
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    '& .MuiSvgIcon-root': {
      width: 24,
      height: 24,
      color: theme.palette.primary.contrastText
    }
  },
  desktopPlayButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    width: "35px",
    height: "35px",
    '& .MuiSvgIcon-root': {
      fontSize: '1.2rem'
    }
  },
  progressContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    minWidth: 0,
    overflow: "hidden"
  },
  waveform: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    height: "20px",
    position: "relative",
    cursor: "pointer",
    width: "100%",
    minWidth: 0,
    overflow: "hidden"
  },
  waveformBar: {
    width: "2px",
    backgroundColor: theme.palette.type === 'light' ? "#e0e0e0" : "#ffffff50",
    borderRadius: "1px"
  },
  progressDot: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: "8px",
    height: "8px",
    backgroundColor: theme.palette.type === 'light' ? '#666' : '#ccc',
    borderRadius: "50%",
    transition: "all 0.1s linear",
    zIndex: 10,
    boxShadow: "0 0 4px rgba(0,0,0,0.3)"
  },
  progressDotPlayed: {
    backgroundColor: '#0084ff' // Azul do WhatsApp
  },
  audioIcon: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 14,
    height: 14,
    backgroundColor: theme.palette.type === 'light' ? '#666' : '#ccc',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    zIndex: 10,
    '& .MuiSvgIcon-root': {
      fontSize: '8px',
      color: 'white'
    }
  },
  audioIconPlayed: {
    backgroundColor: '#0084ff' // Azul do WhatsApp
  },
  
  // Mobile styles
  mobileContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.type === 'light' ? "#f9f9f9" : "#2c2f33",
    padding: "8px",
    borderRadius: "8px",
    boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
    width: "100%"
  },
  mobilePlayButton: {
    color: theme.palette.primary.main,
  },
  mobileRateButton: {
    color: theme.palette.primary.main,
    marginLeft: "10px",
  },
  
  // Common styles
  progressBar: {
    width: "100%",
    height: "4px",
    backgroundColor: theme.palette.type === 'light' ? "#e0e0e0" : "#ffffff50",
    borderRadius: "2px",
    position: "relative",
    cursor: "pointer"
  },
  progress: {
    height: "100%",
    backgroundColor: theme.palette.primary.main,
    borderRadius: "2px",
    position: "absolute",
    transition: "width 0.1s linear"
  },
  timeInfo: {
    display: "flex",
    justifyContent: "space-between",
    color: theme.palette.text.secondary,
    fontSize: "0.75rem"
  },
  downloadButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.dark,
    },
    padding: theme.spacing(0.5),
    '& .MuiSvgIcon-root': {
      fontSize: '1.2rem'
    }
  },
  loadingSpinner: {
    color: theme.palette.primary.contrastText,
    position: 'absolute'
  },
  rateButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    color: theme.palette.primary.contrastText,
    transition: 'all 0.3s ease'
  }
}));

const AudioModal = ({ url, onAudioPlayed, message }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState("0:00");
  const [duration, setDuration] = useState("0:00");
  const [isReady, setIsReady] = useState(false);
  const [audioRate, setAudioRate] = useState(parseFloat(localStorage.getItem(LS_NAME) || "1"));
  const [showButtonRate, setShowButtonRate] = useState(false);
  const [hasBeenPlayed, setHasBeenPlayed] = useState(false);
  // const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isIOS = false;
  
  // Verificar se o áudio foi reproduzido pelo CLIENTE (destinatário)
  // IMPORTANTE: ack === 5 significa que o áudio foi reproduzido pelo cliente
  // ack === 4 = apenas visualizado, ack === 5 = reproduzido
  const isPlayed = message && message.ack === 5 && message.mediaType === "audio";
  
  // Debug: verificar se está sendo aplicado corretamente
  console.log('🎵 AudioModal - message.ack:', message?.ack, 'isPlayed:', isPlayed, 'mediaType:', message?.mediaType, 'messageId:', message?.id);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.playbackRate = audioRate;
    localStorage.setItem(LS_NAME, audioRate);

    const updateProgress = () => {
      if (audio.duration) {
        const progressValue = (audio.currentTime / audio.duration) * 100;
        setProgress(progressValue);
        setCurrentTime(formatTime(audio.currentTime));
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(formatTime(audio.duration));
      setIsReady(true);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime("0:00");
      setShowButtonRate(false);
      
      // Marcar como reproduzido e notificar o componente pai
      if (!hasBeenPlayed) {
        console.log('🎵 Áudio terminou - marcando como reproduzido');
        setHasBeenPlayed(true);
        if (onAudioPlayed) {
          onAudioPlayed();
        }
      }
    };

    const handlePlay = () => {
      setIsLoading(false);
      setIsPlaying(true);
      setShowButtonRate(true);
      // Pausar outros áudios na página
      document.querySelectorAll("audio").forEach(otherAudio => {
        if (otherAudio !== audioRef.current) {
          otherAudio.pause();
        }
      });
    };

    const handlePause = () => {
      setIsLoading(false);
      setIsPlaying(false);
      setShowButtonRate(false);
    };

    const handleWaiting = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("canplay", handleCanPlay);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("canplay", handleCanPlay);
    };
  }, [audioRate]);

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlay = async () => {
    if (!audioRef.current || !isReady) return;

    try {
      if (isPlaying) {
        await audioRef.current.pause();
      } else {
        setIsLoading(true);
        // Se estiver iniciando a reprodução, o handlePlay pausará os outros áudios
        await audioRef.current.play();
      }
    } catch (error) {
      console.error("Error playing audio:", error);
      setIsLoading(false);
      setIsPlaying(false);
    }
  };

  const toggleRate = () => {
    let newRate;
    switch (audioRate) {
      case 0.5: newRate = 1; break;
      case 1: newRate = 1.5; break;
      case 1.5: newRate = 2; break;
      case 2: newRate = 0.5; break;
      default: newRate = 1;
    }
    setAudioRate(newRate);
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current || !isReady) return;

    const progressBar = e.currentTarget;
    const clickPosition = (e.clientX - progressBar.getBoundingClientRect().left) / progressBar.offsetWidth;
    const newTime = clickPosition * audioRef.current.duration;
    audioRef.current.currentTime = newTime;
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const fileName = url.split('/').pop();
      const objectURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectURL;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectURL);
    } catch (error) {
      console.error("Erro ao baixar o arquivo:", error);
    }
  };

  const getAudioSource = () => {
    let sourceUrl = url;
    if (isIOS) {
      sourceUrl = sourceUrl.replace(".ogg", ".mp3");
    }
    return <source src={sourceUrl} type={isIOS ? "audio/mp3" : "audio/ogg"} />;
  };

  if (isMobile) {
    return (
      <div className={classes.mobileContainer}>
        {!isPlaying && (
          <Avatar className={classes.avatar}>
            <Person />
          </Avatar>
        )}
        {isPlaying && (
          <div className={classes.rateButton} onClick={toggleRate}>
            <Speed />
            <span style={{ marginLeft: '4px', fontSize: '12px' }}>{audioRate}x</span>
          </div>
        )}
        <audio ref={audioRef} controls>
          {getAudioSource()}
        </audio>
        <IconButton
          className={classes.downloadButton}
          onClick={handleDownload}
          size="small"
          style={{ marginLeft: 'auto' }}
        >
          <GetApp />
        </IconButton>
      </div>
    );
  }

  return (
    <div className={classes.desktopContainer}>
      {!isPlaying && (
        <div style={{ position: 'relative' }}>
          <Avatar className={classes.avatar}>
            <Person />
          </Avatar>
          <div className={`${classes.audioIcon} ${isPlayed ? classes.audioIconPlayed : ''}`}>
            <Mic />
          </div>
        </div>
      )}
      {isPlaying && (
        <div className={classes.rateButton} onClick={toggleRate}>
          <Speed />
          <span style={{ marginLeft: '4px', fontSize: '12px' }}>{audioRate}x</span>
        </div>
      )}
      
      <audio ref={audioRef} preload="metadata">
        {getAudioSource()}
      </audio>

      <IconButton 
        className={classes.desktopPlayButton}
        onClick={togglePlay}
        disabled={!isReady || isLoading}
        size="small"
      >
        {isLoading ? (
          <CircularProgress size={20} className={classes.loadingSpinner} />
        ) : (
          isPlaying ? <Pause /> : <PlayArrow />
        )}
      </IconButton>

      <div className={classes.progressContainer}>
        <div className={classes.waveform} onClick={handleProgressClick}>
          {[...Array(isMobile ? 20 : 50)].map((_, i) => (
            <div
              key={i}
              className={classes.waveformBar}
              style={{
                height: `${Math.random() * 100}%`,
                backgroundColor: i <= (progress / 100 * (isMobile ? 20 : 50)) 
                  ? (isPlayed ? '#0084ff' : '#666') // Azul WhatsApp quando reproduzido, cinza quando não
                  : theme.palette.type === 'light' ? "#e0e0e0" : "#ffffff50"
              }}
            />
          ))}
          {/* Bolinha de progresso sobre a waveform */}
          <div 
            className={`${classes.progressDot} ${isPlayed ? classes.progressDotPlayed : ''}`}
            style={{ left: `${progress}%` }}
          />
        </div>
        <div className={classes.timeInfo}>
          <span>{currentTime}</span>
          <span>{duration}</span>
        </div>
      </div>

      <IconButton
        className={classes.downloadButton}
        onClick={handleDownload}
        size="small"
      >
        <GetApp />
      </IconButton>
    </div>
  );
};

export default AudioModal;
